import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { cn } from '@/lib/utils';

interface ResponsiveSplitViewProps {
    leftPanel: React.ReactElement;
    rightPanel: React.ReactElement;
    minColumns?: number;
    maxColumns?: number;
    defaultLeftColumns?: number;
    defaultRightColumns?: number;
}

const ResponsiveSplitView: React.FC<ResponsiveSplitViewProps> = ({
    leftPanel,
    rightPanel,
    minColumns = 1,
    maxColumns = 5,
    defaultLeftColumns = 3,
    defaultRightColumns = 3
}) => {
    const [leftColumns, setLeftColumns] = useState(defaultLeftColumns);
    const [rightColumns, setRightColumns] = useState(defaultRightColumns);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const startXRef = useRef<number>(0);
    const startLeftColumnsRef = useRef<number>(0);
    const leftPanelScrollRef = useRef<HTMLDivElement>(null);
    const rightPanelScrollRef = useRef<HTMLDivElement>(null);
    const savedLeftScrollPositionRef = useRef<number>(0);
    const savedRightScrollPositionRef = useRef<number>(0);

    // Save scroll position for left panel
    useEffect(() => {
        const scrollElement = leftPanelScrollRef.current;
        if (scrollElement) {
            const handleScroll = () => {
                savedLeftScrollPositionRef.current = scrollElement.scrollTop;
            };
            scrollElement.addEventListener('scroll', handleScroll);
            return () => scrollElement.removeEventListener('scroll', handleScroll);
        }
    }, []);

    // Save scroll position for right panel
    useEffect(() => {
        const scrollElement = rightPanelScrollRef.current;
        if (scrollElement) {
            const handleScroll = () => {
                savedRightScrollPositionRef.current = scrollElement.scrollTop;
            };
            scrollElement.addEventListener('scroll', handleScroll);
            return () => scrollElement.removeEventListener('scroll', handleScroll);
        }
    }, []);

    // Restore scroll positions after re-renders (use layout effect for synchronous restore)
    useLayoutEffect(() => {
        const leftScrollElement = leftPanelScrollRef.current;
        const rightScrollElement = rightPanelScrollRef.current;
        
        if (leftScrollElement && savedLeftScrollPositionRef.current > 0) {
            leftScrollElement.scrollTop = savedLeftScrollPositionRef.current;
        }
        
        if (rightScrollElement && savedRightScrollPositionRef.current > 0) {
            rightScrollElement.scrollTop = savedRightScrollPositionRef.current;
        }
    });

    const totalColumns = 6; // Using 6-column grid system
    const leftWidth = (leftColumns / totalColumns) * 100;
    const rightWidth = (rightColumns / totalColumns) * 100;

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        startXRef.current = e.clientX;
        startLeftColumnsRef.current = leftColumns;
        
        const handleMouseMove = (e: MouseEvent) => {
            if (!containerRef.current) return;
            
            const containerWidth = containerRef.current.offsetWidth;
            const deltaX = e.clientX - startXRef.current;
            const deltaColumns = Math.round((deltaX / containerWidth) * totalColumns);
            
            const newLeftColumns = Math.max(
                minColumns,
                Math.min(maxColumns, startLeftColumnsRef.current + deltaColumns)
            );
            const newRightColumns = totalColumns - newLeftColumns;
            
            if (newRightColumns >= minColumns && newRightColumns <= maxColumns) {
                setLeftColumns(newLeftColumns);
                setRightColumns(newRightColumns);
            }
        };
        
        const handleMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [leftColumns, minColumns, maxColumns, totalColumns, isDragging]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        startXRef.current = e.touches[0].clientX;
        startLeftColumnsRef.current = leftColumns;
        
        const handleTouchMove = (e: TouchEvent) => {
            if (!containerRef.current) return;
            
            const containerWidth = containerRef.current.offsetWidth;
            const deltaX = e.touches[0].clientX - startXRef.current;
            const deltaColumns = Math.round((deltaX / containerWidth) * totalColumns);
            
            const newLeftColumns = Math.max(
                minColumns,
                Math.min(maxColumns, startLeftColumnsRef.current + deltaColumns)
            );
            const newRightColumns = totalColumns - newLeftColumns;
            
            if (newRightColumns >= minColumns && newRightColumns <= maxColumns) {
                setLeftColumns(newLeftColumns);
                setRightColumns(newRightColumns);
            }
        };
        
        const handleTouchEnd = () => {
            setIsDragging(false);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
        
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
    }, [leftColumns, minColumns, maxColumns, totalColumns, isDragging]);

    return (
        <div 
            ref={containerRef}
            className="h-full flex relative overflow-hidden"
            style={{ overflow: 'hidden' }}
        >
              {/* Left Panel */}
              <div 
                  className="h-full overflow-hidden"
                  style={{ 
                      width: `${(leftColumns / totalColumns) * 100}%`,
                      minWidth: `${(minColumns / totalColumns) * 100}%`,
                      maxWidth: `${(maxColumns / totalColumns) * 100}%`
                  }}
              >
                  <div className="h-full overflow-hidden pr-2">{React.cloneElement(leftPanel as React.ReactElement<any>, { leftColumns })}</div>
              </div>
            
            {/* Draggable Divider */}
            <div
                className={cn(
                    "w-1 bg-primary/20 hover:bg-primary/50 cursor-col-resize transition-colors relative flex-shrink-0",
                    isDragging && "bg-primary"
                )}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
            >
                <div className="w-full h-full flex items-center justify-center">
                    <div className="w-0.5 h-8 bg-muted-foreground/50 rounded-full" />
                </div>
            </div>
            
              {/* Right Panel */}
              <div 
                  className="h-full overflow-hidden flex-1"
                  style={{ 
                      minWidth: `${(minColumns / totalColumns) * 100}%`,
                      maxWidth: `${(maxColumns / totalColumns) * 100}%`
                  }}
              >
                  <div ref={rightPanelScrollRef} className="h-full overflow-auto pl-2">{React.cloneElement(rightPanel as React.ReactElement<any>, { rightColumns })}</div>
              </div>
        </div>
    );
};

export default ResponsiveSplitView;
